// ═══════════════════════════════════════════════════════════════════════════
//  CHART VISION — teaches the bots/agents to READ the SuperChart.
//
//  Every chart pane distills its live candles into a structured ChartRead:
//  trend phase, swing structure (HH/HL vs LH/LL), EMA stack, RSI/MACD
//  momentum, ATR volatility regime, volume behaviour, candle patterns and
//  S/R proximity — the same things a discretionary trader's eye extracts
//  from a chart. The read is fused with the GOD CHAKRA verdict and:
//
//    · cached per symbol@tf + broadcast as a 'chart-read' CustomEvent
//      (client-side agents subscribe / poll getChartRead)
//    · summarised by chartVisionBrief() → injected into every Mamta AI /
//      Pine co-pilot prompt via botMemory.memoryBrief()
//    · pushed (best-effort, throttled) to the backend Mamta AI short-term
//      memory so SERVER-side agents see the same picture
//
//  Uses only candles already on the chart + the shared TA library — no new
//  network calls, no chart drawing. The chart stays clean; the bots get eyes.
// ═══════════════════════════════════════════════════════════════════════════

import { TA } from '../../ChartIndicators';
import type { Candle } from './ChartDataTypes';
import type { GodVerdict } from './godChakra';
import { API } from './chartConstants';

export type ChartPhase = 'TRENDING_UP' | 'TRENDING_DOWN' | 'RANGING' | 'VOLATILE_CHOP';
export type SwingStructure = 'HH_HL' | 'LH_LL' | 'MIXED';

// SMART ENTRY plan — the user's two golden rules, made machine-readable:
//   Rule 1: trade ONLY in the established direction, never counter-trend.
//   Rule 2: price retraces 60-80% of the previous impulse candle most of the
//           time on every TF — so the BEST entry is inside that retrace zone
//           (buy low on pullback, never chase), with exit into strength at
//           the measured-move target before the next giveback.
export interface EntryPlan {
  side: 'BUY' | 'SELL';
  /** best-price zone: the 60-80% retrace band of the impulse candle */
  zoneLo: number;
  zoneHi: number;
  /** invalidation — beyond the impulse extreme the setup is dead */
  sl: number;
  /** T1 = impulse extreme retest, T2 = measured move (1x impulse range) */
  t1: number;
  t2: number;
  /** where price is vs the zone right now */
  status: 'IN_ZONE' | 'WAITING_PULLBACK' | 'INVALIDATED';
  /** time of the impulse candle the plan is built on */
  refTime: number;
}

export interface ChartRead {
  symbol: string;
  tf: string;
  at: number;
  price: number;
  /** market phase from EMA slope vs ATR */
  phase: ChartPhase;
  /** swing structure from fractal highs/lows */
  structure: SwingStructure;
  /** e.g. "P>5>13>50>200" — price position in the EMA stack */
  emaState: string;
  rsi: number | null;
  rsiState: 'OVERBOUGHT' | 'OVERSOLD' | 'BULL' | 'BEAR' | 'NEUTRAL';
  macdHist: 'RISING' | 'FALLING' | 'FLAT' | null;
  /** ATR(14) as % of price */
  atrPct: number | null;
  volRegime: 'HIGH' | 'NORMAL' | 'LOW';
  /** last bar volume vs 20-bar average */
  volRatio: number | null;
  volState: 'SPIKE' | 'HIGH' | 'NORMAL' | 'DRY';
  /** candle patterns on the last bars (live bar included) */
  patterns: string[];
  distSupportPct: number | null;
  distResistancePct: number | null;
  levelNote: 'AT_SUPPORT' | 'AT_RESISTANCE' | null;
  god: { action: string; confluence: number; aligned: boolean } | null;
  /** best-price entry plan (60-80% retrace rule) — null when direction unclear */
  entryPlan: EntryPlan | null;
  /** one-line trader-style summary for AI prompts */
  summary: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const last = <T,>(a: (T | null)[] | undefined): T | null =>
  a && a.length ? a[a.length - 1] : null;

// Fractal swing points: bar i is a swing high/low vs `w` bars on each side.
function swingStructure(c: Candle[], w = 2): SwingStructure {
  const highs: number[] = [], lows: number[] = [];
  for (let i = w; i < c.length - w; i++) {
    let isH = true, isL = true;
    for (let j = i - w; j <= i + w; j++) {
      if (j === i) continue;
      if (c[j].high >= c[i].high) isH = false;
      if (c[j].low <= c[i].low) isL = false;
    }
    if (isH) highs.push(c[i].high);
    if (isL) lows.push(c[i].low);
  }
  if (highs.length < 2 || lows.length < 2) return 'MIXED';
  const hh = highs[highs.length - 1] > highs[highs.length - 2];
  const hl = lows[lows.length - 1] > lows[lows.length - 2];
  if (hh && hl) return 'HH_HL';
  if (!hh && !hl) return 'LH_LL';
  return 'MIXED';
}

function candlePatterns(c: Candle[]): string[] {
  const n = c.length;
  if (n < 3) return [];
  const out: string[] = [];
  const a = c[n - 2], b = c[n - 1];   // a = last closed, b = live/latest
  const body = (x: Candle) => Math.abs(x.close - x.open);
  const range = (x: Candle) => Math.max(1e-9, x.high - x.low);
  const upWick = (x: Candle) => x.high - Math.max(x.open, x.close);
  const dnWick = (x: Candle) => Math.min(x.open, x.close) - x.low;
  const bull = (x: Candle) => x.close > x.open;

  // Engulfing: latest body swallows the previous body, opposite colour.
  if (bull(b) && !bull(a) && b.close >= a.open && b.open <= a.close && body(b) > body(a)) out.push('BULL_ENGULFING');
  if (!bull(b) && bull(a) && b.open >= a.close && b.close <= a.open && body(b) > body(a)) out.push('BEAR_ENGULFING');
  // Pin bars: long single-sided wick, small body.
  if (dnWick(b) > body(b) * 2 && upWick(b) < body(b) && body(b) / range(b) < 0.35) out.push('HAMMER');
  if (upWick(b) > body(b) * 2 && dnWick(b) < body(b) && body(b) / range(b) < 0.35) out.push('SHOOTING_STAR');
  // Doji: body tiny vs range.
  if (body(b) / range(b) < 0.1) out.push('DOJI');
  // Inside bar: latest fully inside previous bar's range.
  if (b.high <= a.high && b.low >= a.low) out.push('INSIDE_BAR');
  // Marubozu: body dominates the range.
  if (body(b) / range(b) > 0.85) out.push(bull(b) ? 'BULL_MARUBOZU' : 'BEAR_MARUBOZU');
  return out;
}

// Rule 2 engine: find the most recent impulse candle in the trade direction
// and build the 60-80% retrace entry zone from it. For a BUY the impulse runs
// low→high, price typically gives back 60-80% of that range, so the best-buy
// band is low + 20%..40% of the range (mirror for SELL). SL sits beyond the
// impulse extreme (setup invalid if the whole candle is retraced), T1 is the
// impulse-extreme retest, T2 the 1x measured move — exit into strength.
function buildEntryPlan(c: Candle[], dir: 1 | -1, atr: number | null, price: number): EntryPlan | null {
  const n = c.length;
  for (let i = n - 2; i >= Math.max(1, n - 12); i--) {
    const x = c[i];
    const range = x.high - x.low;
    if (range <= 0) continue;
    const bull = x.close > x.open;
    if ((dir === 1 && !bull) || (dir === -1 && bull)) continue;
    if (atr != null && range < atr * 0.8) continue;              // real impulse, not noise
    if (Math.abs(x.close - x.open) / range < 0.5) continue;      // conviction body
    let zoneLo: number, zoneHi: number, sl: number, t1: number, t2: number;
    if (dir === 1) {
      zoneLo = x.low + range * 0.2;    // 80% retrace floor
      zoneHi = x.low + range * 0.4;    // 60% retrace ceiling
      sl = x.low - range * 0.1;
      t1 = x.high;
      t2 = x.high + range;
    } else {
      zoneHi = x.high - range * 0.2;
      zoneLo = x.high - range * 0.4;
      sl = x.high + range * 0.1;
      t1 = x.low;
      t2 = x.low - range;
    }
    const status: EntryPlan['status'] =
      (dir === 1 && price <= sl) || (dir === -1 && price >= sl) ? 'INVALIDATED'
      : price >= zoneLo && price <= zoneHi ? 'IN_ZONE'
      : 'WAITING_PULLBACK';
    // Most recent impulse fully retraced = setup dead; older impulses are stale.
    if (status === 'INVALIDATED') return null;
    return { side: dir === 1 ? 'BUY' : 'SELL', zoneLo, zoneHi, sl, t1, t2, status, refTime: x.time };
  }
  return null;
}

// ─── main read ───────────────────────────────────────────────────────────────

// The user's two golden rules, injected verbatim into every AI prompt via
// botMemory.memoryBrief() so every agent is trained on them permanently.
export const TRADING_RULES_BRIEF =
  '[JC TRADING RULES — R1: trade ONLY in the HTF/GOD CHAKRA direction, NEVER counter-trend. ' +
  'R2: price retraces 60-80% of the previous impulse candle on every TF — enter INSIDE the 60-80% retrace zone ' +
  '(buy the pullback, never chase a running candle), SL beyond the impulse extreme, ' +
  'exit into strength at T1 (impulse retest) / T2 (1x measured move) before the giveback erases profit.]';

export function computeChartRead(
  symbol: string,
  tf: string,
  candles: Candle[],
  god?: GodVerdict | null,
  sr?: { support?: { level: number } | null; resistance?: { level: number } | null } | null,
  /** CE/PE when this pane charts an option premium — flips plan direction */
  optionSide?: 'CE' | 'PE' | null,
): ChartRead | null {
  if (!candles || candles.length < 30) return null;
  const c = candles.slice(-300);   // recent window is enough for every metric
  const n = c.length;
  const price = c[n - 1].close;
  if (!(price > 0)) return null;

  // EMA stack + phase
  const e5 = last(TA.ema(c, 5)), e13 = last(TA.ema(c, 13)), e50 = last(TA.ema(c, 50)), e200 = c.length >= 200 ? last(TA.ema(c, 200)) : null;
  const stack = [['5', e5], ['13', e13], ['50', e50], ['200', e200]] as const;
  const above = stack.filter(([, v]) => v != null && price >= (v as number)).length;
  const known = stack.filter(([, v]) => v != null).length;
  const emaState = known
    ? stack.filter(([, v]) => v != null).map(([k, v]) => `${price >= (v as number) ? 'P>' : 'P<'}${k}`).join(' ')
    : 'NA';

  const atrArr = TA.atr(c, 14);
  const atr = last(atrArr);
  const atrPct = atr != null ? (atr / price) * 100 : null;
  // Volatility regime: current ATR vs its own recent median.
  const atrWin = atrArr.slice(-60).filter((v): v is number => v != null).sort((x, y) => x - y);
  const atrMed = atrWin.length ? atrWin[Math.floor(atrWin.length / 2)] : null;
  const volRegime: ChartRead['volRegime'] = atr != null && atrMed
    ? (atr > atrMed * 1.5 ? 'HIGH' : atr < atrMed * 0.6 ? 'LOW' : 'NORMAL') : 'NORMAL';

  // Phase: EMA13 slope over the last 10 bars, normalised by ATR.
  const emaSeries = TA.ema(c, 13);
  const eNow = last(emaSeries);
  const ePrev = emaSeries.length > 10 ? emaSeries[emaSeries.length - 11] : null;
  let phase: ChartPhase = 'RANGING';
  if (eNow != null && ePrev != null && atr != null && atr > 0) {
    const slope = (eNow - ePrev) / (atr * 10);   // ATR-units per bar
    if (slope > 0.08) phase = 'TRENDING_UP';
    else if (slope < -0.08) phase = 'TRENDING_DOWN';
    else phase = volRegime === 'HIGH' ? 'VOLATILE_CHOP' : 'RANGING';
  }

  const rsi = last(TA.rsi(c, 14));
  const rsiState: ChartRead['rsiState'] = rsi == null ? 'NEUTRAL'
    : rsi >= 70 ? 'OVERBOUGHT' : rsi <= 30 ? 'OVERSOLD'
    : rsi >= 55 ? 'BULL' : rsi <= 45 ? 'BEAR' : 'NEUTRAL';

  const macd = TA.macd(c, 12, 26, 9);
  const h0 = last(macd.hist), h1 = macd.hist.length > 1 ? macd.hist[macd.hist.length - 2] : null;
  const macdHist: ChartRead['macdHist'] = h0 == null || h1 == null ? null
    : h0 > h1 ? 'RISING' : h0 < h1 ? 'FALLING' : 'FLAT';

  // Volume behaviour (skip for index charts which tick zero volume).
  const vols = c.slice(-21, -1).map(x => x.volume).filter(v => v > 0);
  const avgVol = vols.length >= 10 ? vols.reduce((a, v) => a + v, 0) / vols.length : 0;
  const volRatio = avgVol > 0 ? c[n - 1].volume / avgVol : null;
  const volState: ChartRead['volState'] = volRatio == null ? 'NORMAL'
    : volRatio >= 2.5 ? 'SPIKE' : volRatio >= 1.4 ? 'HIGH' : volRatio <= 0.5 ? 'DRY' : 'NORMAL';

  // S/R proximity — "near" means within 0.6 ATR of the level.
  const sup = sr?.support?.level, res = sr?.resistance?.level;
  const distSupportPct = sup && sup > 0 ? ((price - sup) / price) * 100 : null;
  const distResistancePct = res && res > 0 ? ((res - price) / price) * 100 : null;
  const nearBand = atr != null ? atr * 0.6 : price * 0.001;
  const levelNote: ChartRead['levelNote'] =
    sup && Math.abs(price - sup) <= nearBand ? 'AT_SUPPORT'
    : res && Math.abs(price - res) <= nearBand ? 'AT_RESISTANCE' : null;

  const patterns = candlePatterns(c);
  const structure = swingStructure(c.slice(-80));
  const godPart = god ? { action: god.action, confluence: god.confluence, aligned: god.aligned } : null;

  // Rule 1: direction comes from GOD CHAKRA first (multi-TF verdict), else a
  // clear trending phase. No clear direction → NO entry plan (never fight the
  // flow, never guess in chop). Option premium panes only ever plan BUY-side
  // (bots buy CE/PE, never short premium): CE needs index BUY, PE needs index
  // SELL; otherwise fall back to the premium's own uptrend.
  let dir: 1 | -1 | 0;
  if (optionSide) {
    const idxDir = godPart?.action === 'BUY' ? 1 : godPart?.action === 'SELL' ? -1 : 0;
    dir = (optionSide === 'CE' && idxDir === 1) || (optionSide === 'PE' && idxDir === -1) ? 1
      : idxDir === 0 && phase === 'TRENDING_UP' ? 1 : 0;
  } else {
    dir = godPart?.action === 'BUY' ? 1 : godPart?.action === 'SELL' ? -1
      : phase === 'TRENDING_UP' ? 1 : phase === 'TRENDING_DOWN' ? -1 : 0;
  }
  const entryPlan = dir === 0 ? null : buildEntryPlan(c, dir, atr, price);

  const bits: string[] = [
    phase.replace('_', ' '),
    structure,
    `EMA ${above}/${known} above`,
    rsi != null ? `RSI ${Math.round(rsi)} ${rsiState}` : '',
    macdHist ? `MACD ${macdHist}` : '',
    `VOL ${volState}${volRatio != null ? ` ${volRatio.toFixed(1)}x` : ''}`,
    volRegime !== 'NORMAL' ? `${volRegime} VOLATILITY` : '',
    levelNote ? levelNote.replace('_', ' ') : '',
    patterns.length ? patterns.join('+') : '',
    godPart ? `GOD ${godPart.action} ${godPart.confluence}%${godPart.aligned ? ' ALIGNED' : ''}` : '',
    entryPlan ? `SMART-ENTRY ${entryPlan.side} zone ${entryPlan.zoneLo.toFixed(1)}-${entryPlan.zoneHi.toFixed(1)} ${entryPlan.status} SL ${entryPlan.sl.toFixed(1)} T1 ${entryPlan.t1.toFixed(1)} T2 ${entryPlan.t2.toFixed(1)}` : '',
  ].filter(Boolean);

  return {
    symbol, tf, at: Date.now(), price,
    phase, structure, emaState,
    rsi, rsiState, macdHist,
    atrPct, volRegime, volRatio, volState,
    patterns, distSupportPct, distResistancePct, levelNote,
    god: godPart,
    entryPlan,
    summary: bits.join(' · '),
  };
}

// ─── Registry + broadcast ────────────────────────────────────────────────────

const _reads = new Map<string, ChartRead>();
const keyOf = (symbol: string, tf: string) => `${symbol}@${tf}`;

export function publishChartRead(read: ChartRead | null, symbol: string, tf: string): void {
  const k = keyOf(symbol, tf);
  if (!read) { _reads.delete(k); return; }
  _reads.set(k, read);
  try { window.dispatchEvent(new CustomEvent('chart-read', { detail: read })); } catch { /* SSR/test safety */ }
}

export function getChartRead(symbol: string, tf?: string): ChartRead | null {
  if (tf) return _reads.get(keyOf(symbol, tf)) || null;
  // No TF given → freshest read for the symbol across panes.
  let best: ChartRead | null = null;
  for (const r of _reads.values()) if (r.symbol === symbol && (!best || r.at > best.at)) best = r;
  return best;
}

export function getAllChartReads(): ChartRead[] {
  return Array.from(_reads.values());
}

/** Compact prompt line for Mamta AI / Pine co-pilot — fresh reads only. */
export function chartVisionBrief(maxAgeMs = 120000): string {
  const fresh = getAllChartReads().filter(r => Date.now() - r.at < maxAgeMs);
  if (!fresh.length) return '';
  const rows = fresh.slice(0, 6).map(r => `${r.symbol}@${r.tf}: ${r.summary}`);
  return `[CHART VISION (SuperChart live read) — ${rows.join(' | ')}]`;
}

// ─── Backend STM push — server-side agents see the same picture ─────────────
// Mirrors the screener's /mamta-ai/smc-context push (best-effort, throttled).
let _lastPush = 0;

export function pushChartVisionToServer(): void {
  const now = Date.now();
  if (now - _lastPush < 30000) return;
  const fresh = getAllChartReads().filter(r => now - r.at < 120000);
  if (!fresh.length) return;
  _lastPush = now;
  const snapshot = fresh.slice(0, 8).map(r => ({
    sym: r.symbol, tf: r.tf, px: r.price,
    trend: r.phase, structure: r.structure,
    rsi: r.rsi != null ? Math.round(r.rsi) : null,
    vol: r.volState, patt: r.patterns.join('+') || null,
    god: r.god ? `${r.god.action} ${r.god.confluence}%` : null,
    read: r.summary,
  }));
  fetch(`${API}/mamta-ai/smc-context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ snapshot, mode: 'chart-vision', ts: now }),
    keepalive: true,
  }).catch(() => { /* STM is best-effort */ });
}
